import {
  Button,
  FileUpload,
  FileUploadContent,
  FileUploadTrigger,
  PromptInput,
  PromptInputAction,
  PromptInputActions,
  PromptInputTextarea
} from "@prelude-ai/design-system"
import { ArrowUp, Loader2, Paperclip, X } from "lucide-react"
import type { StagedAttachment } from "../types.ts"

type InputAreaProps = {
  input: string
  setInput: (value: string) => void
  onSend?: () => void
  isLoading: boolean
  className?: string
  attachments?: StagedAttachment[]
  onAttach?: (files: File[]) => void
  onRemoveAttachment?: (id: string) => void
  isUploading?: boolean
}

export default function InputArea({
  input,
  setInput,
  onSend,
  isLoading,
  className,
  attachments = [],
  onAttach,
  onRemoveAttachment,
  isUploading = false
}: InputAreaProps) {
  const canSend = (input.trim() || attachments.length > 0) && !isLoading && !isUploading

  const content = (
    <div className={className}>
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 px-2 pb-2">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex items-center gap-1.5 rounded-md border border-border bg-muted px-2.5 py-1 text-sm"
            >
              {attachment.status === "uploading" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              ) : (
                <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
              )}
              <span className="max-w-[200px] truncate">{attachment.name}</span>
              {onRemoveAttachment && attachment.status !== "uploading" && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={`Remove ${attachment.name}`}
                  onClick={() => onRemoveAttachment(attachment.id)}
                  className="ml-0.5 h-5 w-5 rounded-sm text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <PromptInput
        className="rounded-sm"
        value={input}
        onValueChange={setInput}
        onSubmit={canSend ? onSend : undefined}
        isLoading={isLoading}
        disabled={isLoading}
      >
        <PromptInputTextarea
          placeholder="Type your message..."
          className="min-h-[56px] border border-border px-4 py-3 text-base"
        />
        <PromptInputActions className="flex justify-end gap-1 px-2 py-2">
          {onAttach && (
            <PromptInputAction tooltip="Attach file">
              <FileUploadTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="rounded-full"
                  aria-label="Attach file"
                  disabled={isLoading}
                >
                  <Paperclip className="h-5 w-5" />
                </Button>
              </FileUploadTrigger>
            </PromptInputAction>
          )}
          <PromptInputAction tooltip={isUploading ? "Uploading..." : isLoading ? "Sending..." : "Send message"}>
            <Button
              size="icon"
              className="rounded-full"
              aria-label="Send message"
              aria-description={isUploading ? "Uploading..." : isLoading ? "Generating..." : "Send"}
              disabled={!canSend}
              onClick={onSend}
            >
              <ArrowUp className="h-5 w-5 rounded-full" />
            </Button>
          </PromptInputAction>
        </PromptInputActions>
      </PromptInput>
    </div>
  )

  if (!onAttach) return content
  return (
    <FileUpload onFilesAdded={onAttach} multiple disabled={isLoading}>
      {content}
      <FileUploadContent>
        <div className="rounded-lg border-2 border-dashed border-primary bg-background px-8 py-6 text-sm font-medium">
          Drop files to attach
        </div>
      </FileUploadContent>
    </FileUpload>
  )
}
